import { EventBus }     from '../../core/EventBus.js';
import { GameState }    from '../../core/GameState.js';
import { Modal }        from '../components/Modal.js';
import { CARGO_TYPES }  from '../../config/cargoTypes.js';
import { WAREHOUSE_COSTS, WAREHOUSE_UPGRADES } from '../../config/warehouseCosts.js';
import { isFeatureUnlocked } from '../../config/research.js';
import { formatMoney }  from '../../utils/formatting.js';
import { showAlert }    from '../components/dialogs.js';
import { serverIntent } from '../../core/serverIntent.js';

const LEVEL_LABELS = { 1: 'Level 1 (Medium berth)', 2: 'Level 2 (Large berth)', 3: 'Level 3 (Major berth)' };
const LEVEL_TEU    = { 1: '2,000', 2: '8,000', 3: 'Unlimited' };

export class WarehouseModal {
  constructor({ warehouseSystem }) {
    this.warehouseSystem = warehouseSystem;
    this.modal = new Modal('warehouse-modal', { title: 'Warehouse' });
    this.port = null;
    this._selectedShipId = null;

    EventBus.on('ui:warehouseOpen', ({ port }) => this.open(port));
  }

  open(port) {
    this.port = port;
    this._selectedShipId = null;
    this._render();
    this.modal.open();
  }

  _render() {
    const port = this.port;
    if (!port) return;

    const wh = [...GameState.warehouses.values()].find(w => w.portId === port.id) || null;
    const costs = WAREHOUSE_COSTS[port.size];

    if (!wh) {
      this._renderBuild(port, costs);
      return;
    }

    // Warehouse exists — show transfer UI
    const dockedShips = [...(port.dockedShips || [])]
      .map(id => GameState.getShip(id))
      .filter(ship => ship && ship.status === 'docked');

    if (!this._selectedShipId && dockedShips.length > 0) {
      this._selectedShipId = dockedShips[0].id;
    }
    const selectedShip = this._selectedShipId ? GameState.getShip(this._selectedShipId) : null;

    let shipSelect = '<select id="wh-ship-select">';
    if (dockedShips.length === 0) {
      shipSelect += '<option value="">— No ships docked —</option>';
    }
    for (const s of dockedShips) {
      const sel = s.id === this._selectedShipId ? ' selected' : '';
      const feeFree = wh.canDockFeeFree(s) ? ' (fee-free berth)' : '';
      shipSelect += `<option value="${s.id}"${sel}>${s.name} (${s.getAvailableCapacity()}/${s.getCargoCapacity()} TEU free)${feeFree}</option>`;
    }
    shipSelect += '</select>';

    // Warehouse cargo rows with average prices
    let whCargoRows = '';
    if (wh.cargo.length === 0) {
      whCargoRows = '<p class="panel-empty">Empty</p>';
    } else {
      whCargoRows = wh.cargo.map((c, idx) => {
        const def = CARGO_TYPES[c.type];
        const avg = wh.cargoAverages[c.type];
        const priceLabel = avg ? formatMoney(Math.round(avg.avgPrice)) + '/TEU avg' : formatMoney(c.purchasePrice) + '/TEU';
        return `<div class="wh-cargo-row">
          <span>${c.quantity} TEU <strong>${def?.name || c.type}</strong>
            <span class="wh-cargo-price">${priceLabel}</span></span>
          <button class="btn btn-sm btn-primary" data-wh-load="${idx}">Load to Ship</button>
        </div>`;
      }).join('');
    }

    // Ship cargo rows
    let shipCargoRows = '';
    if (!selectedShip) {
      shipCargoRows = '<p class="panel-empty">No ship selected</p>';
    } else if (selectedShip.cargo.length === 0) {
      shipCargoRows = '<p class="panel-empty">Empty hold</p>';
    } else {
      shipCargoRows = selectedShip.cargo.map((c, idx) => {
        const def = CARGO_TYPES[c.type];
        return `<div class="wh-cargo-row">
          <span>${c.quantity} TEU <strong>${def?.name || c.type}</strong>
            <span class="wh-cargo-price">${formatMoney(c.purchasePrice)}/TEU</span></span>
          <button class="btn btn-sm btn-danger" data-wh-store="${idx}">Store</button>
        </div>`;
      }).join('');
    }

    // Upgrade section
    let upgradeHtml = '';
    if (wh.level < 3) {
      const nextLevel = wh.level + 1;
      const upgrades = WAREHOUSE_UPGRADES[port.size];
      const upgrade = upgrades?.[nextLevel];
      const hasResearch = isFeatureUnlocked('warehouse_upgrades');
      if (upgrade) {
        const canAfford = GameState.player.canAfford(upgrade.cost);
        const nextBerth = LEVEL_TEU[nextLevel] || '?';
        upgradeHtml = `
          <div class="wh-upgrade">
            <h5>Upgrade to Level ${nextLevel}</h5>
            <div class="wh-upgrade-details">
              <span>+${upgrade.capacityBonus} TEU capacity</span>
              <span>Berth: ${nextBerth} TEU</span>
              <span>+${formatMoney(upgrade.maintenanceIncrease)}/mo</span>
            </div>
            ${!hasResearch
              ? '<p class="wh-upgrade-locked">Requires: Warehouse Expansion research</p>'
              : `<button class="btn btn-sm btn-primary" id="btn-upgrade-wh" ${!canAfford ? 'disabled' : ''}>
                  Upgrade (${formatMoney(upgrade.cost)})
                </button>`
            }
          </div>`;
      }
    }

    this.modal.setBody(`
      <div class="wh-ui">
        <div class="wh-header-info">
          <p class="wh-info">${port.name} — ${wh.cargoUsed}/${wh.capacity} TEU</p>
          <p class="wh-level">${LEVEL_LABELS[wh.level] || 'Level ' + wh.level} — Max ship: ${LEVEL_TEU[wh.level] || '?'} TEU</p>
        </div>
        ${upgradeHtml}
        ${shipSelect}

        <h5>Warehouse Stock</h5>
        ${whCargoRows}

        <h5>Ship Cargo ${selectedShip ? `(${selectedShip.cargoCapacityUsed}/${selectedShip.getCargoCapacity()} TEU)` : ''}</h5>
        ${shipCargoRows}
      </div>`);

    this._bindActions(wh, port);
  }

  _renderBuild(port, costs) {
    if (!costs) {
      this.modal.setBody(`<p class="panel-empty">Cannot build a warehouse at ${port.size} ports.</p>`);
      return;
    }
    this.modal.setBody(`
      <div class="wh-build">
        <h4>Build Warehouse at ${port.name}</h4>
        <table class="stats-table">
          <tr><td>Capacity</td><td>${costs.capacity} TEU</td></tr>
          <tr><td>Build Cost</td><td>${formatMoney(costs.buildCost)}</td></tr>
          <tr><td>Monthly Maintenance</td><td>${formatMoney(costs.monthlyMaintenance)}</td></tr>
          <tr><td>Berth</td><td>Medium (ships up to 2,000 TEU dock fee-free)</td></tr>
        </table>
        <p>Your funds: <strong>${formatMoney(GameState.player.money)}</strong></p>
        <button class="btn btn-primary" id="btn-build-wh"
          ${!GameState.player.canAfford(costs.buildCost) ? 'disabled' : ''}>
          Build Warehouse (${formatMoney(costs.buildCost)})
        </button>
      </div>`);

    const buildBtn = document.getElementById('btn-build-wh');
    if (buildBtn) {
      buildBtn.addEventListener('click', async () => {
        const result = await serverIntent('warehouse/build', { portId: port.id });
        if (!result.success) { return; }
        this._render();
      });
    }
  }

  _bindActions(wh, port) {
    // Upgrade button
    const upgradeBtn = document.getElementById('btn-upgrade-wh');
    if (upgradeBtn) {
      upgradeBtn.addEventListener('click', async () => {
        const result = await serverIntent('warehouse/upgrade', { warehouseId: wh.id });
        if (!result.success) return;
        this._render();
      });
    }

    const shipSelectEl = document.getElementById('wh-ship-select');
    if (shipSelectEl) {
      shipSelectEl.addEventListener('change', () => {
        this._selectedShipId = shipSelectEl.value;
        this._render();
      });
    }

    // Store in warehouse (ship → warehouse)
    document.querySelectorAll('[data-wh-store]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const idx = Number(btn.dataset.whStore);
        const shipId = this._selectedShipId;
        if (!shipId) return;
        const ship = GameState.getShip(shipId);
        if (!ship || !ship.cargo[idx]) return;
        const qty = ship.cargo[idx].quantity;
        const result = await serverIntent('warehouse/store', { shipId, cargoIndex: idx, quantity: qty, portId: port.id });
        if (!result.success) return;
        this._render();
      });
    });

    // Load from warehouse (warehouse → ship)
    document.querySelectorAll('[data-wh-load]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const idx = Number(btn.dataset.whLoad);
        const shipId = this._selectedShipId;
        if (!shipId) return;
        const ship = GameState.getShip(shipId);
        if (!ship || !wh.cargo[idx]) return;
        const qty = wh.cargo[idx].quantity;
        const result = await serverIntent('warehouse/load', { warehouseId: wh.id, cargoIndex: idx, quantity: qty, shipId });
        if (!result.success) return;
        this._render();
      });
    });
  }
}
